import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import TransactionHistoryTable from '../../../components/credits/TransactionHistoryTable';
import {
  getAiSpendEvents, pivotEvents, withMargin,
  type AiSpendEventRow, type MarginRow, type PivotDimension,
} from '../../../services/aiControlApi';
import { RubyLoader } from '../../../components/RubyLoader';

const usd = (n: number, dp = 4): string => `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(dp)}`;

const SECONDARY_DIMENSIONS: PivotDimension[] = ['tenant', 'service', 'feature', 'role', 'model'];

interface Props {
  row: MarginRow;
  dimension: PivotDimension;
  range: { fromIso?: string; toIso?: string };
  creditUnitPriceUsd: number;
  onClose: () => void;
}

/**
 * One pivot row expanded: the same spend re-pivoted by a second dimension,
 * plus the raw events. For a tenant row the existing credit-ledger audit
 * table is shown too — it is the itemized, exportable record.
 *
 * The secondary pivot is computed from the events already fetched, so
 * changing it never costs another request.
 */
const TenantSpendDrilldown: React.FC<Props> = ({
  row, dimension, range, creditUnitPriceUsd, onClose,
}) => {
  const [events, setEvents] = useState<AiSpendEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [secondary, setSecondary] = useState<PivotDimension>(
    dimension === 'tenant' ? 'feature' : 'tenant',
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getAiSpendEvents({
          ...(dimension === 'tenant' ? { tenantId: row.key } : {}),
          ...(dimension === 'role' ? { roleId: row.key } : {}),
          ...(dimension === 'model' ? { modelId: row.key.split('#')[1] } : {}),
          fromIso: range.fromIso,
          toIso: range.toIso,
          limit: 200,
        });
        if (!cancelled) setEvents(res.items);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load events');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [row.key, dimension, range.fromIso, range.toIso]);

  const secondaryRows = withMargin(pivotEvents(events, secondary), creditUnitPriceUsd);

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div className="bg-stone-50 rounded-xl shadow-2xl w-full max-w-5xl my-8"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-white rounded-t-xl">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{row.label}</h3>
            <p className="text-[11px] text-gray-500">
              {usd(row.costUsd)} vendor cost · {row.requests.toLocaleString()} calls · charged and margin are on the Ruby Ledger
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="text-gray-400 hover:text-gray-600 min-h-[44px] px-2">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2 text-xs">
              {error}
            </div>
          )}

          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b border-stone-100 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">
                Break this down by
              </span>
              {SECONDARY_DIMENSIONS
                .filter((d) => d !== dimension)
                .map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setSecondary(d)}
                    className={`px-2.5 py-2 min-h-[44px] rounded-lg text-[11px] font-semibold border transition-colors ${
                      secondary === d
                        ? 'bg-emerald-600 border-emerald-600 text-white'
                        : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    {d}
                  </button>
                ))}
            </div>
            {loading ? (
              <RubyLoader variant="section" />
            ) : (
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr className="text-[10px] uppercase tracking-wide text-gray-400">
                    <th className="px-3 py-1.5 text-left font-medium">{secondary}</th>
                    <th className="px-3 py-1.5 text-right font-medium">Calls</th>
                    <th className="px-3 py-1.5 text-right font-medium">Vendor cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {secondaryRows.map((r) => (
                    <tr key={r.key}>
                      <td className="px-3 py-1.5 text-xs text-gray-800">{r.label}</td>
                      <td className="px-3 py-1.5 text-xs text-right text-gray-700">{r.requests}</td>
                      <td className="px-3 py-1.5 text-xs text-right text-gray-900">{usd(r.costUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {dimension === 'tenant' && (
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-stone-100">
                <h4 className="text-xs font-semibold text-gray-900">Credit ledger — every transaction</h4>
                <p className="text-[10px] text-gray-500">
                  The itemized, exportable record. Exports require super-admin claims
                  and write an audit row.
                </p>
              </div>
              <div className="p-3">
                <TransactionHistoryTable
                  tenantId={row.key}
                  context="admin"
                  tenantDisplayName={row.label}
                  initialTimeRange={{ preset: '30d' }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TenantSpendDrilldown;
